import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// ─── Tutorial Step Definition ───────────────────────────────────────────────
export interface TutorialStep {
    id: string;
    titleKey: string;
    descriptionKey: string;
    targetView?: string;       // navigates user to this view
    highlightSelector?: string; // CSS selector to spotlight
    position?: 'center' | 'bottom-right' | 'bottom-left' | 'top-right';
    icon?: string;              // lucide icon name
}

const TUTORIAL_STEPS: TutorialStep[] = [
    {
        id: 'welcome',
        titleKey: 'tutorial.welcome_title',
        descriptionKey: 'tutorial.welcome_desc',
        position: 'center',
        icon: 'Sparkles',
    },
    {
        id: 'dashboard',
        titleKey: 'tutorial.dashboard_title',
        descriptionKey: 'tutorial.dashboard_desc',
        targetView: 'dashboard',
        position: 'center',
        icon: 'LayoutDashboard',
    },
    {
        id: 'create-replica',
        titleKey: 'tutorial.create_replica_title',
        descriptionKey: 'tutorial.create_replica_desc',
        targetView: 'dashboard',
        highlightSelector: '[data-tutorial="create-replica"]',
        position: 'bottom-right',
        icon: 'UserPlus',
    },
    {
        id: 'my-replica',
        titleKey: 'tutorial.my_replica_title',
        descriptionKey: 'tutorial.my_replica_desc',
        targetView: 'my-replica',
        highlightSelector: '[data-tutorial="nav-my-replica"]',
        position: 'bottom-right',
        icon: 'User',
    },
    {
        id: 'training',
        titleKey: 'tutorial.training_title',
        descriptionKey: 'tutorial.training_desc',
        targetView: 'my-data',
        highlightSelector: '[data-tutorial="nav-my-data"]',
        position: 'bottom-right',
        icon: 'Database',
    },
    {
        id: 'sovereignty',
        titleKey: 'tutorial.sovereignty_title',
        descriptionKey: 'tutorial.sovereignty_desc',
        targetView: 'dashboard',
        highlightSelector: '[data-tutorial="sovereignty"]',
        position: 'center',
        icon: 'Shield',
    },
    {
        id: 'messages',
        titleKey: 'tutorial.messages_title',
        descriptionKey: 'tutorial.messages_desc',
        targetView: 'messages',
        highlightSelector: '[data-tutorial="nav-messages"]',
        position: 'bottom-right',
        icon: 'MessageSquare',
    },
    {
        id: 'integrations',
        titleKey: 'tutorial.integrations_title',
        descriptionKey: 'tutorial.integrations_desc',
        targetView: 'integrations',
        highlightSelector: '[data-tutorial="nav-integrations"]',
        position: 'bottom-right',
        icon: 'Plug',
    },
    {
        id: 'workspace',
        titleKey: 'tutorial.workspace_title',
        descriptionKey: 'tutorial.workspace_desc',
        highlightSelector: '[data-tutorial="workspace-switcher"]',
        position: 'bottom-right',
        icon: 'Building',
    },
    {
        id: 'complete',
        titleKey: 'tutorial.complete_title',
        descriptionKey: 'tutorial.complete_desc',
        targetView: 'dashboard',
        position: 'center',
        icon: 'PartyPopper',
    },
];

// ─── Context Shape ──────────────────────────────────────────────────────────
interface TutorialContextType {
    isActive: boolean;
    currentStep: number;
    steps: TutorialStep[];
    totalSteps: number;
    startTutorial: () => void;
    nextStep: () => void;
    prevStep: () => void;
    skipTutorial: () => void;
    completeTutorial: () => void;
    hasCompleted: boolean;
}

const TutorialContext = createContext<TutorialContextType | null>(null);

const STORAGE_KEY = 'humanreplica_tutorial_completed';

// ─── Provider ───────────────────────────────────────────────────────────────
export const TutorialProvider: React.FC<{
    children: React.ReactNode;
    onNavigate?: (view: string) => void;
}> = ({ children, onNavigate }) => {
    const [isActive, setIsActive] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [hasCompleted, setHasCompleted] = useState(() => {
        return localStorage.getItem(STORAGE_KEY) === 'true';
    });

    // Auto-launch on first visit (after a short delay to let dashboard render)
    useEffect(() => {
        if (!hasCompleted) {
            const timer = setTimeout(() => {
                setIsActive(true);
                setCurrentStep(0);
            }, 1200);
            return () => clearTimeout(timer);
        }
    }, [hasCompleted]);

    const startTutorial = useCallback(() => {
        setCurrentStep(0);
        setIsActive(true);
    }, []);

    const nextStep = useCallback(() => {
        const nextIdx = currentStep + 1;
        if (nextIdx >= TUTORIAL_STEPS.length) {
            // Reached the end
            setIsActive(false);
            setHasCompleted(true);
            localStorage.setItem(STORAGE_KEY, 'true');
            return;
        }
        setCurrentStep(nextIdx);
        // Navigate to the step's target view if specified
        const step = TUTORIAL_STEPS[nextIdx];
        if (step.targetView && onNavigate) {
            onNavigate(step.targetView);
        }
    }, [currentStep, onNavigate]);

    const prevStep = useCallback(() => {
        const prevIdx = Math.max(0, currentStep - 1);
        setCurrentStep(prevIdx);
        const step = TUTORIAL_STEPS[prevIdx];
        if (step.targetView && onNavigate) {
            onNavigate(step.targetView);
        }
    }, [currentStep, onNavigate]);

    const skipTutorial = useCallback(() => {
        setIsActive(false);
        setHasCompleted(true);
        localStorage.setItem(STORAGE_KEY, 'true');
    }, []);

    const completeTutorial = useCallback(() => {
        setIsActive(false);
        setHasCompleted(true);
        localStorage.setItem(STORAGE_KEY, 'true');
        if (onNavigate) {
            onNavigate('dashboard');
        }
    }, [onNavigate]);

    return (
        <TutorialContext.Provider value={{
            isActive,
            currentStep,
            steps: TUTORIAL_STEPS,
            totalSteps: TUTORIAL_STEPS.length,
            startTutorial,
            nextStep,
            prevStep,
            skipTutorial,
            completeTutorial,
            hasCompleted,
        }}>
            {children}
        </TutorialContext.Provider>
    );
};

export const useTutorial = (): TutorialContextType => {
    const ctx = useContext(TutorialContext);
    if (!ctx) throw new Error('useTutorial must be used within a TutorialProvider');
    return ctx;
};

export default TutorialContext;
